<script setup>
import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * M3-styled radio button with label positioning and error state support.
 *
 * @status recommended
 * @group forms-input
 * @useWhen Selecting exactly one option from a small set; use HrOptionGroup for data-driven radio lists
 * @related HrOptionGroup
 * @see https://quasar.dev/vue-components/radio Quasar q-radio API
 */
defineOptions({ name: 'HrRadio' });

/**
 * @typedef {'left' | 'right'} HRRadioLabelPosition
 */
const props = defineProps({
  /** Currently selected value; this radio is checked when `modelValue === val`. */
  modelValue: {
    type: null,
    default: null,
  },
  /** This radio's value emitted on selection. Required. */
  val: {
    type: null,
    required: true,
  },
  /** Label text shown beside the radio button. */
  label: {
    type: String,
    default: null,
  },
  /**
   * Position of the label relative to the radio button.
   * @values left, right
   */
  labelPosition: {
    type: String,
    default: 'right',
    validator: (v) => ['left', 'right'].includes(v),
  },
  /** Prevents interaction and dims the radio button. */
  disable: {
    type: Boolean,
    default: false,
  },
  /**
   * Switches the color role to error. Use to indicate invalid selection state.
   */
  error: {
    type: Boolean,
    default: false,
  },
  /**
   * Legacy props are any additional Quasar props that can be passed to the radio for backwards
   * compatibility with the legacy theme. These props will be ignored when a design system theme
   * is active.
   * @type {Object} legacyProps
   * @deprecated This prop is only for backwards compatibility with the legacy theme and should be
   * removed once the legacy theme is removed.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits({
  /** Emitted when this radio is selected; payload is `val`. */
  'update:modelValue': null,
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const classes = computed(() => [
  'hr-radio',
  props.error && 'hr-radio--error',
]);

const effectiveProps = computed(() => {
  const base = {
    modelValue: props.modelValue,
    val: props.val,
    ...(props.label !== null && { label: props.label }),
    leftLabel: props.labelPosition === 'left',
    disable: props.disable,
  };

  if (hasTheme.value) {
    return { ...base, ...attrs };
  }
  return { ...base, ...props.legacyProps, ...attrs };
});
</script>

<template>
  <q-radio
    v-bind="effectiveProps"
    :class="hasTheme ? classes : undefined"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <!-- @slot default Custom label content; use instead of the `label` prop for rich content. -->
    <slot />
  </q-radio>
</template>

<style lang="scss" scoped>

/* Only applies to light/dark themes */

.hr-radio {
  --hr-radio-ring:  20px;
  --hr-radio-layer: 40px;
}

/* ── Inner container ─────────────────────────────────────────────────── */
/*
 * font-size: 1px neutralises Quasar's em-based proportional sizing.
 * color drives currentColor throughout: SVG ring + inner dot paths both
 * use fill: currentColor. Setting it here fixes everything in one place.
 */
.hr-radio :deep(.q-radio__inner) {
  font-size: 1px;
  width: var(--hr-radio-layer);
  height: var(--hr-radio-layer);
  color: var(--app-on-surface-variant);
}

/* Override Quasar's hardcoded --q-primary */
.hr-radio :deep(.q-radio__inner--truthy) {
  color: var(--app-primary);
}

/* ── State layer ─────────────────────────────────────────────────────── */
/*
 * Quasar's ::before uses background: currentColor — correct via the inner
 * color fix above. Reduce hover opacity from Quasar's 12% to M3's 8%.
 * Unselected hover uses on-surface (not on-surface-variant) per M3 spec.
 * body.desktop mirrors Quasar's own selector to win on specificity.
 */
/* stylelint-disable-next-line selector-max-type */
body.desktop .hr-radio:not(.disabled):hover :deep(.q-radio__inner::before) {
  opacity: 0.08;
}

/* stylelint-disable-next-line selector-max-type */
body.desktop .hr-radio:not(.disabled):hover :deep(.q-radio__inner--falsy::before),
/* stylelint-disable-next-line selector-max-type */
body.desktop .hr-radio:not(.disabled):focus-within :deep(.q-radio__inner--falsy::before) {
  background: var(--app-on-surface);
}

/* ── Label ───────────────────────────────────────────────────────────── */
.hr-radio :deep(.q-radio__label) {
  padding-left: 8px;
  color: var(--app-on-surface);
}

.hr-radio.reverse :deep(.q-radio__label) {
  padding-left: 0;
  padding-right: 8px;
}

/* ── Error ───────────────────────────────────────────────────────────── */
/*
 * Set color on all inner states so currentColor cascades to both SVG paths
 * (ring and dot) without further overrides.
 */
.hr-radio--error :deep(.q-radio__inner),
.hr-radio--error :deep(.q-radio__inner--truthy) {
  color: var(--app-error);
}

/* ── Disabled ────────────────────────────────────────────────────────── */
/* Override Quasar's blanket opacity: 0.75 !important */
.hr-radio.disabled {
  opacity: 1 !important;
}

.hr-radio.disabled :deep(.q-radio__inner) {
  color: color-mix(in srgb, var(--app-on-surface) 38%, transparent);
}

.hr-radio.disabled :deep(.q-radio__label) {
  color: color-mix(in srgb, var(--app-on-surface) 38%, transparent);
}

</style>
